import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';

import AppContext from '../../../context';
import { ReactComponent as Cross } from '../../../../assets/icons/cross.svg';
import { ReactComponent as TickCircleGreen } from '../../../../assets/icons/tick-circle-green.svg';
import { ReactComponent as Refresh } from '../../../../assets/icons/refresh.svg';
import { PhoneNumberUtil } from 'google-libphonenumber';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import OtpInput from 'react-otp-input';
import './import_mfs.component.css';

import { requestMFOtp, verifyMFOtp } from '../../../../api';

const ImportMFs = (props) => {
  // Context
  const context = useContext(AppContext);
  const { pf, show, setShow, setContext, defaultStep } = props;

  const [step, setStep] = useState(defaultStep ?? 'request_otp');
  const [PAN, setPAN] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isPANValid, setIsPANValid] = useState(false);
  const [showIsValid, setShowIsValid] = useState(false);
  const [requestOtpError, setRequestOtpError] = useState('');
  const [txnId, setTxnId] = useState('');
  const phoneUtil = PhoneNumberUtil.getInstance();

  const [otp, setOtp] = useState('');
  const [isOtpValid, setIsOtpValid] = useState(true);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [verifyOtpError, setVerifyOtpError] = useState('');

  const [mfFetchStatus, setMfFetchStatus] = useState(null);
  const resendOtpTimerId = useRef(null);

  useEffect(() => {
    if (
      PAN.length === 10 &&
      /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/.test(PAN)
    ) {
      setIsPANValid(true);
      return;
    }
    setIsPANValid(false);
  }, [PAN]);

  useEffect(() => {
    setShowIsValid(false);
    if (country && phone.length > country.dialCode.length + 1) {
      const countryCode = country.countryCode;
      const countryDial = country.dialCode.length;
      const num = phoneUtil.parseAndKeepRawInput(
        phone.slice(countryDial),
        countryCode,
      );
      if (phoneUtil.isValidNumberForRegion(num, countryCode)) {
        setIsPhoneValid(true);
        return;
      }
      setIsPhoneValid(false);
    }
  }, [phone]);

  useEffect(() => {
    if (requestOtpError) {
      setRequestOtpError('');
    }
    if (showIsValid) {
      setShowIsValid(false);
    }
  }, [phone, PAN]);

  useEffect(() => {
    if (!show) {
      setStep('request_otp');
      setPAN('');
      setPhone('');
      setOtp('');
      setShowIsValid(false);
      setRequestOtpError('');
      setIsSendingOtp(false);
      setVerifyOtpError('');
      setIsOtpValid(true);
    }
  }, [show]);

  useEffect(() => {
    if (mfFetchStatus === 'init') {
      let pfs = _.cloneDeep(context.portfolios);
      const pfIdx = context.portfolios.findIndex((_pf) => _pf.id == pf.id);
      pfs[pfIdx].is_mf_sync_in_progress = true;
      setContext({ portfolios: pfs });

      const timeout = 300; // 300 seconds timeout
      const eventSource = new EventSource(
        `${window.location.origin}/api/portfolio/task-status/?timeout=${timeout}`,
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.status === 'DONE') {
          eventSource.close();
          // Make another network call with 'acked' parameter
          fetch(
            `${window.location.origin}/api/portfolio/task-status/?acked=true`,
          );
          setMfFetchStatus('done');

          let pfs = _.cloneDeep(context.portfolios);
          const pfIdx = context.portfolios.findIndex((_pf) => _pf.id == pf.id);
          pfs[pfIdx].is_mf_sync_in_progress = false;
          setContext({ portfolios: pfs, showMfSyncSuccessPf: pfs[pfIdx] });
        } else if (data.status === 'TIMEOUT') {
          console.log('Task status check timed out');
          eventSource.close();
        } else if (data.status === 'NOPFCREATED') {
          console.log('could not create portfolio');
          eventSource.close();
        }
      };
    }
  }, [mfFetchStatus]);

  const requestOTP = async () => {
    setRequestOtpError('');
    if (isPANValid && isPhoneValid) {
      try {
        setIsSendingOtp(true);
        const res = await requestMFOtp({
          pfId: pf.id,
          pan: PAN.toUpperCase(),
          mobile: `+${phone}`,
        });
        setTxnId(res.data.transactionId);
        setStep('verify_otp');
      } catch (error) {
        console.log(error);
        let errText = 'Something went wrong. Please try again';
        if (error.response && error.response.status === 422) {
          errText = "PAN or Mobile number doesn't match";
        }
        if (error?.response?.data?.error) {
          errText = error.response.data.error;
        }
        setRequestOtpError(errText);
        setIsSendingOtp(false);
      }
      return;
    }
    setShowIsValid(true);
  };

  const enableResendOtpTimer = () => {
    let rem = 30;
    setIsResendDisabled(true);
    setResendTimer(rem);
    const intervalId = setInterval(() => {
      rem -= 1;
      setResendTimer(rem);
      if (rem === 0) {
        clearInterval(intervalId);
        setIsResendDisabled(false);
        return;
      }
    }, 1000);
    resendOtpTimerId.current = intervalId;
  };

  const handleResendOTP = async () => {
    try {
      const res = await requestMFOtp({
        pan: PAN.toUpperCase(),
        mobile: `+${phone}`,
        pfId: pf.id,
      });
      setTxnId(res.data.transactionId);
      enableResendOtpTimer();
      setIsSendingOtp(false);
    } catch (err) {
      setRequestOtpError('Something went wrong. Please try again later');
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      setIsOtpValid(false);
      return;
    }
    try {
      setIsVerifyingOtp(true);
      const res = await verifyMFOtp({
        pan: PAN.toUpperCase(),
        mobile: phone.slice(2),
        otp,
        txn_id: txnId,
        pfId: pf.id,
      });
      if (res.status === 200) {
        setStep('fetching_funds');
        setMfFetchStatus('init');
      }
    } catch (err) {
      setIsVerifyingOtp(false);
      if (
        err.response &&
        err.response.data &&
        err.response.data.errors.length
      ) {
        setVerifyOtpError(err.response.data.errors[0]);
        return;
      }
      setVerifyOtpError('Something went wrong. Please try again later');
      return;
    }
  };

  const areAllPfsEmpty = () => {
    return !context.portfolios.some(
      (pf) => !pf.is_empty && pf.is_empty !== undefined,
    );
  };

  const otpErrorMap = {
    TransactionIdExpired: `OTP has expired. Please request a new OTP to continue`,
    'Invalid token or client Id': 'Invalid OTP',
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      animation={false}
      className="import_mfs__modal"
      onHide={() => {
        if (resendOtpTimerId.current) clearInterval(resendOtpTimerId.current);
        setShow(false);
      }}
    >
      <Modal.Body>
        <div className="content_header">
          <h4>Fetch your Mutual Funds</h4>
          {step === 'fetching_funds' ? null : (
            <button
              type="button"
              className="back_btn"
              onClick={() => setShow(false)}
            >
              <Cross />
            </button>
          )}
        </div>
        {pf &&
          (pf.is_mf_eligible_to_sync ? (
            <div className="content_body">
              {step === 'request_otp' && (
                <>
                  <div className="mb-4">
                    <label>PAN</label>
                    <input
                      type="text"
                      placeholder="ABCDE1234F"
                      value={PAN}
                      onKeyDown={(e) => {
                        if (!/^[A-Za-z0-9]+$/.test(e.key)) e.preventDefault();
                      }}
                      onChange={(e) => setPAN(e.target.value)}
                      className={`pan_input`}
                    />
                    {showIsValid && !isPANValid && (
                      <p className="text-danger mt-1">Invalid PAN</p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label>Mobile Number</label>
                    <PhoneInput
                      country={'in'}
                      value={phone}
                      disableDropdown={true}
                      countryCodeEditable={false}
                      onChange={(value, country) => {
                        setPhone(value);
                        setCountry(country);
                      }}
                      className={`phone_input`}
                    />
                    {showIsValid && !isPhoneValid && (
                      <p className="text-danger mt-1">Invalid Mobile number</p>
                    )}
                  </div>
                  <p>All holdings with PAN card will be fetched</p>
                  <div className="position-relative mt-4">
                    {requestOtpError && (
                      <p className="error_text text-danger mt-1">
                        {requestOtpError}
                      </p>
                    )}
                    <button
                      type="button"
                      className="request_otp"
                      onClick={requestOTP}
                      disabled={isSendingOtp}
                    >
                      Confirm and Get OTP
                    </button>
                  </div>
                </>
              )}
              {step === 'verify_otp' && (
                <>
                  <label>OTP has been sent to {`+${phone}`}</label>
                  <div className="position-relative">
                    <OtpInput
                      value={otp}
                      onChange={(otp) => {
                        if (!isOtpValid) setIsOtpValid(true);
                        if (verifyOtpError) setVerifyOtpError('');
                        setOtp(otp);
                      }}
                      numInputs={6}
                      containerStyle="otp-inputs-cont mt-4 mb-3"
                      inputStyle={`otp-input`}
                      inputType="tel"
                      shouldAutoFocus={true}
                      renderInput={(props) => <input {...props} />}
                    />
                    {verifyOtpError && (
                      <p className="text-danger position-absolute">
                        {otpErrorMap[verifyOtpError] ?? verifyOtpError}
                      </p>
                    )}
                    {!isOtpValid && (
                      <p className="text-danger position-absolute">
                        Please enter OTP
                      </p>
                    )}
                  </div>
                  <div className="position-relative mt-5 mb-2 otp-form-links">
                    <div className="otp-resend-cont">
                      <button
                        type="button"
                        className="otp-resend"
                        onClick={handleResendOTP}
                        disabled={isResendDisabled}
                      >
                        Resend OTP
                      </button>
                      {!!resendTimer && <span> in {resendTimer}s</span>}
                    </div>
                  </div>
                  <div className="position-relative mt-5">
                    <button
                      type="button"
                      className="verify_otp"
                      onClick={handleVerifyOtp}
                      disabled={isVerifyingOtp}
                    >
                      Verify OTP
                    </button>
                  </div>
                </>
              )}
              {step === 'fetching_funds' && (
                <div className="fetching_funds">
                  {mfFetchStatus === 'done' ? (
                    <>
                      <TickCircleGreen />
                      <p>Your Mutual Funds are successfully fetched</p>
                      <button
                        type="button"
                        className="close_and_reload"
                        onClick={() => window.location.reload()}
                      >
                        Close and Reload Portfolio
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="fetching_icon">
                        <Refresh />
                      </span>
                      <p className="text-center">
                        Fetching Mutual Funds, this will take 10-15 minutes. We
                        will notify you once done.
                      </p>
                      {!areAllPfsEmpty() && (
                        <button
                          type="button"
                          className="close_and_reload"
                          onClick={() => setShow(false)}
                        >
                          Close
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="content_body">{pf.mf_sync_message}</div>
          ))}
      </Modal.Body>
    </Modal>
  );
};

export default ImportMFs;
